// src/utils/processors.js

import { normalizeKey, hasMeaningfulValue, isPassValue, isFailValue } from './helpers';
import { DOA_PATTERNS, SCAN_PATTERNS, DATE_PATTERNS } from '../constants';

export const isDoaAuditRow = (row) => {
  const values = Object.values(row || {}).map(v => normalizeKey(v));
  return values.some(v => DOA_PATTERNS.audit.some(pattern => pattern.test(v)));
};

export const isValidDoaParameterValue = (value) => {
  const v = normalizeKey(value);
  return DOA_PATTERNS.validValues.includes(v);
};

export const parseStrictScanLines = (raw) => {
  if (!raw || !raw.trim()) return null;

  const lines = raw.split('\n').map(l => l.trim()).filter(Boolean);
  const results = [];

  for (const line of lines) {
    const userMatch = line.match(SCAN_PATTERNS.userPrefix);
    if (!userMatch) continue;

    const userId = userMatch[1];
    const result = { userName: userId, userId, date: null, time: null };

    const isoMatch = line.match(SCAN_PATTERNS.isoDateTime);
    if (isoMatch) {
      result.date = isoMatch[1];
      result.time = isoMatch[2];
      results.push(result);
      continue;
    }

    const shortMatch = line.match(SCAN_PATTERNS.shortDateTime);
    if (shortMatch) {
      const year = new Date().getFullYear();
      result.date = `${year}-${shortMatch[1]}-${shortMatch[2]}`;
      result.time = shortMatch[3];
      results.push(result);
    }
  }

  return results.length > 0 ? results : null;
};

export const extractDatesFromScanCell = (raw) => {
  if (!raw || !raw.trim()) return [];

  const dates = [];
  const lines = raw.split('\n').map(l => l.trim()).filter(Boolean);

  for (const line of lines) {
    if (!SCAN_PATTERNS.userPrefix.test(line)) continue;

    const isoMatch = line.match(SCAN_PATTERNS.isoDateTime);
    if (isoMatch) {
      dates.push(isoMatch[1]);
    } else {
      const shortMatch = line.match(SCAN_PATTERNS.shortDateTime);
      if (shortMatch) {
        dates.push(`${new Date().getFullYear()}-${shortMatch[1]}-${shortMatch[2]}`);
      }
    }
  }

  return dates.sort().reverse();
};

export const getRowDate = (row = {}) => {
  const allDates = [];

  const pushDateFromValue = (value) => {
    const raw = String(value || '').trim();
    if (!raw) return;

    const isoMatch = raw.match(/(\d{4}-\d{2}-\d{2})/);
    if (isoMatch) {
      allDates.push(isoMatch[1]);
      return;
    }

    const slashMatch = raw.match(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
    if (slashMatch) {
      const day = slashMatch[1].padStart(2, '0');
      const month = slashMatch[2].padStart(2, '0');
      let year = slashMatch[3];

      if (year.length === 2) year = `20${year}`;

      allDates.push(`${year}-${month}-${day}`);
    }
  };

  Object.entries(row).forEach(([key, value]) => {
    const lowerKey = String(key || '').toLowerCase();

    if (
      lowerKey.includes('timestamp') ||
      lowerKey.includes('time stamp') ||
      lowerKey.includes('date') ||
      lowerKey.includes('updated') ||
      lowerKey.includes('created')
    ) {
      pushDateFromValue(value);
    }

    pushDateFromValue(value);

    const scanDates = extractDatesFromScanCell(String(value || ''));
    allDates.push(...scanDates);
  });

  return allDates.length ? allDates.sort().reverse()[0] : null;
};

export const extractStatus = (raw) => {
  if (!raw || !raw.trim()) return '';

  const lines = raw
    .replace(/[\uFEFF\u00A0\u200B]/g, '')
    .split('\n')
    .map(l => l.trim())
    .filter(Boolean);

  for (const line of lines) {
    if (SCAN_PATTERNS.userPrefix.test(line)) continue;
    if (DATE_PATTERNS.timestamp?.test(line)) continue;
    if (DATE_PATTERNS.usShort?.test(line)) continue;
    return line.toLowerCase();
  }

  return '';
};

export const buildTechActivity = (data, maxActivities = 20) => {
  const scanColumns = ['Condition', 'PSU-1 Cond', 'PSU-2 Cond'];
  const activities = [];

  data.forEach(row => {
    scanColumns.forEach(col => {
      const entries = parseStrictScanLines(row[col] || '');
      if (entries) {
        activities.push(...entries);
      }
    });
  });

  activities.sort((a, b) => {
    const dateA = a.date && a.time ? `${a.date} ${a.time}` : '';
    const dateB = b.date && b.time ? `${b.date} ${b.time}` : '';
    return dateB.localeCompare(dateA);
  });

  // Remove duplicates
  const seen = new Set();
  return activities
    .filter(activity => {
      const key = `${activity.userName}|${activity.date}|${activity.time}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, maxActivities);
};